// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SupportAlert } from '@/components/support-alert';
import {
  CloudDoneOutlined,
  CloudUploadOutlined,
  HourglassEmptyOutlined,
  Upload,
} from '@/constants/hardfin-standard-icons';
import { formatDuration } from '@/utilities/format-duration';
import { formatNumber } from '@/utilities/number';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface CsvUploadProps {
  file: File | null;
  isUploading: boolean;
  onSelectFile: () => void;
  onUpload: () => void;
  onBack: () => void;
  onClose?: () => void;
  hasFatalError?: boolean;
  uploadErrorMessage: string | null;
  uploadLineCount?: number;
}

/**
 * Renders the CSV upload step of the item data upload process
 */
export function CsvUpload(props: CsvUploadProps) {
  const estimatedUploadTime = props.uploadLineCount
    ? Math.round(props.uploadLineCount / 200) + 2
    : 0;

  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleUpload = () => {
    props.onUpload();
    setCountdown(estimatedUploadTime);
  };

  useEffect(() => {
    if (props.isUploading && countdown > 0 && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setCountdown((prevCount) => prevCount - 1);
      }, 1000);
    }

    if (!props.isUploading) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setCountdown(0);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [props.isUploading, countdown]);

  return (
    <Card>
      <CardContent>
        {props.uploadErrorMessage && (
          <Box sx={{ my: 2 }}>
            <SupportAlert message={props.uploadErrorMessage} />
          </Box>
        )}

        {props.hasFatalError ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Upload failed</AlertTitle>
            Something went wrong with your upload, support has been notified.
          </Alert>
        ) : (
          <>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: 'center',
                border: '2px dashed rgba(0, 0, 0, 0.12)',
                cursor: 'pointer',
                mb: 3,
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
              onClick={props.onSelectFile}
            >
              {props.file ? (
                <CloudDoneOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              ) : (
                <CloudUploadOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              )}
              <Typography variant="h6" gutterBottom>
                {props.file
                  ? props.file.name
                  : 'Upload a CSV file with your item data'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {props.file
                  ? `Ready to upload ${formatNumber(props.uploadLineCount ?? 0)} items (${formatNumber(Math.round(props.file.size / 1024))} KB)`
                  : 'Click to browse for your CSV file'}
              </Typography>
            </Paper>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Important notes</AlertTitle>
              <ul
                style={{
                  paddingLeft: '1.5rem',
                  marginTop: '1rem',
                  marginBottom: '0.5rem',
                }}
              >
                <li>
                  Names and SKUs must be unique - duplicates within the file or
                  with existing items will be rejected
                </li>
                <li>
                  BULK items require a valid unit of measure (e.g., EA, KGM,
                  MTR)
                </li>
                <li>
                  Accounting fields can only be provided for DEVICE items, not
                  SERVICE or BULK
                </li>
              </ul>
            </Alert>
          </>
        )}

        {estimatedUploadTime >= 60 && !props.isUploading && (
          <Alert severity="warning" sx={{ mb: 2, width: '50%', mx: 'auto' }}>
            <AlertTitle>This data looks large</AlertTitle>
            Consider breaking the file into smaller pieces for reliability
          </Alert>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignContent="center"
        >
          <Button
            variant="outlined"
            onClick={props.onBack}
            disabled={props.isUploading}
          >
            Back
          </Button>
          <Box display="flex" alignItems="center">
            {props.uploadLineCount && !props.isUploading && (
              <Typography color="text.secondary">
                Estimated processing time: {formatDuration(estimatedUploadTime)}
              </Typography>
            )}
            {props.isUploading && countdown > 0 && (
              <Box display="flex" alignItems="center" gap={1}>
                <HourglassEmptyOutlined
                  sx={{
                    color: 'text.secondary',
                    fontSize: 20,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        opacity: 0.4,
                      },
                      '50%': {
                        opacity: 1,
                      },
                    },
                  }}
                />
                <Typography color="text.secondary">
                  Estimated time remaining: {formatDuration(countdown)}
                </Typography>
              </Box>
            )}
            {props.isUploading && countdown === 0 && (
              <Typography color="text.secondary">Almost finished...</Typography>
            )}
          </Box>
          {props.hasFatalError && props.onClose ? (
            <Button variant="contained" onClick={props.onClose}>
              Close
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={
                props.isUploading ? <CircularProgress size={20} /> : <Upload />
              }
              onClick={handleUpload}
              disabled={!props.file || props.isUploading}
            >
              {props.isUploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
